import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, FileDown, MailCheck, Upload, Loader2, QrCode, Eye, UserPlus, CalendarPlus, ChevronDown, ChevronUp, Clock, CalendarClock, Mail } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CreateBookingModal } from "@/components/CreateBookingModal";
import CampaignMailout from "@/components/admin/CampaignMailout";
import LetterABPanel from "@/components/admin/LetterABPanel";
import { RenameCampaignButton, SafeDeleteCampaignButton, InviteResetButton } from "@/components/admin/CampaignCleanup";
import { EditLeadDialog } from "@/components/admin/EditLeadDialog";
import { ResendReportButton } from "@/components/admin/ResendReportButton";
import { TechnicianWeekGrid } from "@/components/admin/TechnicianWeekGrid";
import PostcardStudioCampaignPanel from "@/components/admin/PostcardStudioCampaignPanel";

const card = "rounded-2xl bg-white border border-slate-200 p-5";

function StatCard({ label, value, sub, tid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={tid}>
      <div className="text-2xl font-bold text-slate-900">{value}{" "}</div>
      <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">{label}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  "Booking Created": "bg-emerald-600 text-white",
  "Customer Created": "bg-emerald-100 text-emerald-800 border border-emerald-300",
  "Appointment Booked": "bg-cyan-700 text-white",
  "Inspection Completed": "bg-emerald-700 text-white",
  "Lead Captured": "bg-cyan-600 text-white",
  "Eligible": "bg-cyan-100 text-cyan-800 border border-cyan-300",
  "Not Eligible": "bg-slate-200 text-slate-600",
  "Scanned": "bg-amber-100 text-amber-800 border border-amber-300",
  "Not Scanned": "bg-slate-100 text-slate-500",
};

function statusOf(i, appt) {
  if (i.booking_reference) return "Booking Created";
  if (i.converted_customer_id) return "Customer Created";
  if (appt && appt.status === "completed") return "Inspection Completed";
  if (appt) return "Appointment Booked";
  if (i.lead_created_at) return "Lead Captured";
  if (i.eligibility_answer === "no") return "Not Eligible";
  if (i.eligibility_answer) return "Eligible";
  if (i.first_scan_at) return "Scanned";
  return "Not Scanned";
}

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null);
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : null);
const pct = (v) => (v == null ? "—" : `${Math.round(v * 1000) / 10}%`);
const fmtTime12 = (hhmm) => {
  const h = parseInt(hhmm.slice(0, 2), 10);
  return `${h % 12 || 12}:${hhmm.slice(3)}${h < 12 ? "am" : "pm"}`;
};
const fmtSlotDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

export function PropertyCampaignsList() {
  const [items, setItems] = useState([]);
  const [autoSend, setAutoSend] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", suburb: "", state: "QLD", partner_id: "", notes: "" });
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refQuery, setRefQuery] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [mobiles, setMobiles] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newMobile, setNewMobile] = useState("");
  const navigate = useNavigate();

  const saveRecipients = async (emails, mobs) => {
    try {
      const { data } = await api.put("/admin/property-campaigns/booking-alert-recipients", { emails, mobiles: mobs });
      setRecipients(data.emails);
      setMobiles(data.mobiles || []);
      toast.success("Booking alert recipients saved");
    } catch (err) {
      const status = err.response?.status;
      const detail = typeof err.response?.data?.detail === "string" ? err.response.data.detail : null;
      toast.error(detail || (status ? `Save failed (HTTP ${status})` : "Save failed — network error"));
    }
  };

  const addRecipient = (e) => {
    e.preventDefault();
    const v = newEmail.trim();
    if (!v) return;
    saveRecipients([...(recipients || []), v], mobiles);
    setNewEmail("");
  };

  const addMobile = (e) => {
    e.preventDefault();
    const v = newMobile.trim();
    if (!v) return;
    saveRecipients(recipients || [], [...mobiles, v]);
    setNewMobile("");
  };

  const refLookup = async (e) => {
    e.preventDefault();
    if (!refQuery.trim()) return;
    setRefBusy(true);
    try {
      const { data: r } = await api.get(`/admin/property-campaign-ref-lookup?ref=${encodeURIComponent(refQuery.trim())}`);
      toast.success(`${r.street_address}, ${r.suburb} — ${r.campaign_name}`);
      navigate(`/admin/property-campaigns/${r.campaign_id}?invite=${r.invite_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ref lookup failed");
    } finally { setRefBusy(false); }
  };

  const load = useCallback(() => {
    api.get("/admin/property-campaigns").then(({ data }) => setItems(data.campaigns)).catch(() => {});
    api.get("/admin/partners?partner_type=pws_franchisee").then(({ data }) => setPartners(data.partners || data || [])).catch(() => {});
    api.get("/admin/property-campaigns/report-auto-send").then(({ data }) => setAutoSend(data)).catch(() => {});
    api.get("/admin/property-campaigns/booking-alert-recipients").then(({ data }) => { setRecipients(data.emails || []); setMobiles(data.mobiles || []); }).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const toggleAutoSend = async () => {
    const enabling = !autoSend?.enabled;
    if (enabling && !window.confirm(
      `Turn ON report auto-send?\n\nInspection reports will be emailed to customers automatically ${autoSend?.delay_minutes ?? 10} minutes after the technician completes the inspection — without manual review/approval. Only inspections completed from now on are affected.`)) return;
    try {
      const { data } = await api.put("/admin/property-campaigns/report-auto-send", { enabled: enabling });
      setAutoSend(data);
      toast.success(`Report auto-send ${enabling ? "ON" : "off"}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/property-campaigns", { ...form, partner_id: form.partner_id || null });
      toast.success("Campaign created");
      navigate(`/admin/property-campaigns/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="property-campaigns-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-slate-900">Property Campaigns</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/address-database" data-testid="pc-address-db-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5">
            <Upload className="w-4 h-4" /> Address Database
          </Link>
          <Link to="/admin/postcard-studio" data-testid="pc-postcard-studio-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900 rounded-full border border-cyan-200 bg-cyan-50 px-3.5 py-1.5">
            <Mail className="w-4 h-4" /> Postcard Studio
          </Link>
          <Link to="/admin/settings/servicem8" data-testid="pc-servicem8-settings-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900 rounded-full border border-cyan-200 bg-cyan-50 px-3.5 py-1.5">
            <CalendarClock className="w-4 h-4" /> ServiceM8 Scheduling
          </Link>
        </div>
      </div>
      <div className="mt-4">
        <TechnicianWeekGrid />
      </div>
      {autoSend && (
        <div className={`mt-4 rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 ${autoSend.enabled ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid="pc-auto-send-card">
          <div>
            <div className="font-semibold text-slate-900 text-sm">
              Report auto-send: {autoSend.enabled ? <span className="text-emerald-700">ON</span> : <span className="text-slate-500">off</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {autoSend.enabled
                ? `Reports email automatically ${autoSend.delay_minutes} minutes after inspection completion — no manual approval. Applies to inspections completed since ${autoSend.enabled_at ? new Date(autoSend.enabled_at).toLocaleString() : "now"}.`
                : "When on, inspection reports send to customers automatically 10 minutes after the technician completes — without review/approval."}
            </div>
          </div>
          <button data-testid="pc-auto-send-toggle" onClick={toggleAutoSend}
            className={`px-4 py-2 rounded-full text-sm font-bold border ${autoSend.enabled ? "border-slate-300 text-slate-600 hover:bg-white" : "border-emerald-600 text-emerald-700 hover:bg-emerald-50"}`}>
            {autoSend.enabled ? "Turn off" : "Turn on auto-send"}
          </button>
        </div>
      )}
      {recipients !== null && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="pc-booking-alert-card">
          <div className="font-semibold text-slate-900 text-sm">Booking alert recipients</div>
          <div className="text-xs text-slate-500 mt-0.5 mb-3">
            Every <span className="font-semibold">new</span> Water Health Check booking emails these addresses (in addition to the campaign partner).
            Tip: add your ServiceM8 Email Inbox address (ServiceM8 → Settings → Email Inbox) and new bookings will land in your ServiceM8 Inbox and mobile app — the subject includes the property address and date so it's easy to attach to the job.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {recipients.map((em) => (
              <span key={em} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700" data-testid={`pc-alert-recipient-${em}`}>
                {em}
                <button type="button" data-testid={`pc-alert-remove-${em}`} title="Remove"
                  onClick={() => saveRecipients(recipients.filter((x) => x !== em))}
                  className="text-slate-400 hover:text-rose-600 font-bold leading-none">×</button>
              </span>
            ))}
            {recipients.length === 0 && (
              <span className="text-xs text-slate-400 italic">No extra recipients yet</span>
            )}
          </div>
          <form onSubmit={addRecipient} className="mt-3 flex gap-2 max-w-md">
            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="name@example.com" data-testid="pc-alert-email-input"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            <button type="submit" data-testid="pc-alert-add-btn"
              className="rounded-full bg-cyan-700 text-white text-sm font-bold px-4 py-1.5 hover:bg-cyan-800">
              Add
            </button>
          </form>
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="font-semibold text-slate-900 text-sm">SMS alerts</div>
            <div className="text-xs text-slate-500 mt-0.5 mb-3">
              These mobiles get a text the moment a <span className="font-semibold">new</span> booking lands — address, date and time included. AU numbers only.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {mobiles.map((m) => (
                <span key={m} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid={`pc-alert-mobile-${m}`}>
                  {m}
                  <button type="button" data-testid={`pc-alert-mobile-remove-${m}`} title="Remove"
                    onClick={() => saveRecipients(recipients || [], mobiles.filter((x) => x !== m))}
                    className="text-emerald-400 hover:text-rose-600 font-bold leading-none">×</button>
                </span>
              ))}
              {mobiles.length === 0 && (
                <span className="text-xs text-slate-400 italic">No SMS recipients yet</span>
              )}
            </div>
            <form onSubmit={addMobile} className="mt-3 flex gap-2 max-w-md">
              <input type="tel" value={newMobile} onChange={(e) => setNewMobile(e.target.value)}
                placeholder="04xx xxx xxx" data-testid="pc-alert-mobile-input"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
              <button type="submit" data-testid="pc-alert-mobile-add-btn"
                className="rounded-full bg-emerald-700 text-white text-sm font-bold px-4 py-1.5 hover:bg-emerald-800">
                Add mobile
              </button>
            </form>
          </div>
        </div>
      )}
      <form onSubmit={refLookup} className={`${card} mt-5 flex flex-wrap items-center gap-2`} data-testid="pc-ref-lookup-form">
        <QrCode className="w-4 h-4 text-cyan-700" />
        <span className="text-sm font-semibold text-slate-700">Letter Ref lookup</span>
        <input data-testid="pc-ref-lookup-input" placeholder="Ref code from a letter footer (e.g. ab12cd34)" value={refQuery} onChange={(e) => setRefQuery(e.target.value)}
          className="flex-1 min-w-[220px] rounded-xl border border-slate-300 px-3 py-2 text-sm font-mono" />
        <Button data-testid="pc-ref-lookup-btn" type="submit" disabled={refBusy || !refQuery.trim()} variant="outline" className="rounded-full">
          {refBusy ? "Searching…" : "Find property"}
        </Button>
      </form>
      <form onSubmit={create} className={`${card} mt-5 grid sm:grid-cols-2 gap-3`}>
        <input data-testid="pc-name" required placeholder="Campaign Name (e.g. Redwood Drive – August 2026)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm sm:col-span-2" />
        <input data-testid="pc-code" required placeholder="Code (REDWOOD-AUG26)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        <input data-testid="pc-suburb" placeholder="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        <select data-testid="pc-partner" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm">
          <option value="">No partner</option>
          {(partners || []).map((p) => <option key={p.id} value={p.id}>{p.business_name}</option>)}
        </select>
        <Button data-testid="pc-create" disabled={busy} type="submit" className="rounded-full bg-slate-900"><Plus className="w-4 h-4 mr-1" /> Create Campaign</Button>
      </form>
      <div className="mt-6 space-y-3">
        {items.map((c) => (
          <Link key={c.id} to={`/admin/property-campaigns/${c.id}`} data-testid={`pc-row-${c.code}`} className={`${card} flex items-center justify-between hover:border-cyan-300 transition-colors`}>
            <div>
              <div className="font-bold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500 font-mono">{c.code} · {c.status}</div>
            </div>
            <div className="flex items-center gap-3">
              {c.pending_reviews > 0 && (
                <span data-testid={`pc-pending-badge-${c.code}`} className="px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold">
                  {c.pending_reviews} report{c.pending_reviews > 1 ? "s" : ""} awaiting review
                </span>
              )}
              <div className="text-sm text-slate-600">{c.stats?.properties_imported ?? 0} properties · {c.stats?.leads_captured ?? 0} leads</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const CONCERN_LABELS = {
  taste_smell: "Taste or smell", colour_staining: "Colour or staining",
  sediment_cloudy: "Sediment / cloudy water", filters_blocking: "Filters blocking quickly",
  tank_not_cleaned: "Tank not cleaned in a long time", unsure_last_tested: "Unsure when last tested",
  no_specific: "No specific concern", other: "Other",
};

function InviteTimeline({ invite, appt }) {
  const events = [];
  const push = (at, label) => at && events.push({ at, label });
  push(invite.created_at, "Property imported");
  push(invite.letter_generated_at, "Letter generated");
  push(invite.mailed_at, `Marked mailed${invite.mailed_by ? ` by ${invite.mailed_by}` : ""}`);
  push(invite.first_scan_at, "QR first scanned");
  push(invite.eligibility_answered_at, `Eligibility: ${(invite.eligibility_answer || "").replace("_", " ").toUpperCase()}`);
  if (invite.water_concerns_answered_at) {
    const cl = (invite.water_concerns || []).filter((k) => !(k === "other" && invite.water_concerns_other)).map((k) => CONCERN_LABELS[k] || k).join(" · ");
    push(invite.water_concerns_answered_at, `Concerns: ${cl}${invite.water_concerns_other ? `${cl ? " — " : ""}"${invite.water_concerns_other}"` : ""}`);
  }
  push(invite.lead_created_at, "Lead submitted");
  if (invite.notification_sent_at) {
    if (invite.admin_notification_status === "sent") push(invite.notification_sent_at, "Admin notified");
    if (invite.partner_notification_status === "sent") push(invite.notification_sent_at, "Partner notified");
  }
  (invite.resubmissions || []).forEach((r) => push(r.at, "Details re-submitted"));
  if (invite.ebook_email_status === "sent") push(invite.ebook_email_sent_at, "Free guide emailed");
  push(invite.ebook_first_downloaded_at, `Free guide downloaded${(invite.ebook_download_count || 0) > 1 ? ` (${invite.ebook_download_count}×)` : ""}`);
  push(invite.appointment_selection_viewed_at, "Appointment times viewed");
  if (appt) {
    push(appt.booked_at, `Appointment booked — ${fmtSlotDay(appt.date)} ${fmtTime12(appt.start_time)}`);
    push(appt.confirmation_email_sent_at, "Confirmation email sent");
    push(appt.rescheduled_at, `Appointment moved to ${fmtSlotDay(appt.date)} ${fmtTime12(appt.start_time)}`);
    push(appt.reminder_email_sent_at, "Reminder email sent");
  }
  push(invite.appointment_cancelled_at, "Appointment cancelled");
  push(invite.customer_converted_at, `Customer created${invite.converted_by ? ` by ${invite.converted_by}` : ""}`);
  push(invite.booking_converted_at, `Booking ${invite.booking_reference || ""} created`);
  events.sort((a, b) => (a.at < b.at ? -1 : 1));
  return (
    <div data-testid="pc-invite-timeline" className="space-y-1.5">
      {events.map((e, idx) => (
        <div key={idx} className="flex items-start gap-2 text-xs">
          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
          <span className="text-slate-500 font-mono whitespace-nowrap">{fmtDT(e.at)}</span>
          <span className="text-slate-800 font-medium">{e.label}</span>
        </div>
      ))}
      {(invite.admin_notification_status === "failed" || invite.partner_notification_status === "failed") && (
        <div data-testid="pc-notification-error" className="text-xs text-rose-600 font-semibold mt-1">
          Notification issue: {invite.notification_error || `admin ${invite.admin_notification_status || "—"} / partner ${invite.partner_notification_status || "—"}`}
        </div>
      )}
    </div>
  );
}

const DELIVERY_CHIP = {
  clicked: ["Clicked", "bg-emerald-600 text-white"],
  opened: ["Opened", "bg-emerald-100 text-emerald-800 border border-emerald-300"],
  delivered: ["Delivered", "bg-emerald-100 text-emerald-800 border border-emerald-300"],
  delivery_delayed: ["Delayed", "bg-amber-100 text-amber-800 border border-amber-300"],
  bounced: ["Bounced", "bg-rose-100 text-rose-800 border border-rose-300"],
  complained: ["Spam report", "bg-rose-100 text-rose-800 border border-rose-300"],
  failed: ["Failed", "bg-rose-100 text-rose-800 border border-rose-300"],
  canceled: ["Cancelled", "bg-slate-200 text-slate-600"],
  queued: ["Queued", "bg-slate-100 text-slate-600 border border-slate-200"],
  scheduled: ["Queued", "bg-slate-100 text-slate-600 border border-slate-200"],
  sent: ["Sent", "bg-slate-100 text-slate-600 border border-slate-200"],
  skipped_no_key: ["Not sent", "bg-slate-200 text-slate-600"],
  suppressed: ["Suppressed", "bg-slate-200 text-slate-600"],
};

function DeliveryChip({ email }) {
  const key = email.delivery_status || email.status;
  const [label, cls] = DELIVERY_CHIP[key] || [key || "—", "bg-slate-100 text-slate-500"];
  return <span data-testid="pc-delivery-chip" className={`shrink-0 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`}>{label}</span>;
}

function EmailDeliveryList({ emails, readRestricted }) {
  if (!emails?.length) return null;
  return (
    <div data-testid="pc-email-delivery" className="mt-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Email delivery</div>
      <div className="space-y-1.5">
        {emails.map((e, idx) => (
          <div key={idx} className="flex items-start gap-2 text-xs">
            <Mail className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <span className="text-slate-800 font-medium">{e.subject || e.category}</span>
              <span className="text-slate-400 break-all"> → {e.to}</span>
            </div>
            <DeliveryChip email={e} />
          </div>
        ))}
      </div>
      {readRestricted && (
        <div data-testid="pc-delivery-restricted-note" className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 leading-relaxed">
          Delivery tracking (Delivered / Bounced / Opened) needs a Resend API key with read access. Your current key is send-only — create a "Full access" key at resend.com → API Keys and set it as RESEND_READ_API_KEY.
        </div>
      )}
    </div>
  );
}


function ConvertPanel({ campaignId, invite, onDone, onCreateBooking }) {
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/property-campaigns/${campaignId}/invites/${invite.id}/customer-matches`)
      .then(({ data }) => setMatches(data.matches)).catch(() => setMatches([]));
  }, [campaignId, invite.id]);

  const createCustomer = async (useExistingId = null) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/property-campaigns/${campaignId}/invites/${invite.id}/convert-customer`,
        useExistingId ? { use_existing_user_id: useExistingId } : {});
      toast.success(data.created ? "Customer created" : "Linked to existing customer");
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Conversion failed");
    } finally { setBusy(false); }
  };

  const userMatch = (matches || []).find((m) => m.type === "user");
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Convert Lead</div>
      {matches === null ? (
        <div className="text-xs text-slate-400">Checking for existing customers…</div>
      ) : (
        <>
          {matches.length > 0 && (
            <div data-testid="pc-match-warning" className="mb-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
              <b>Possible existing match{matches.length > 1 ? "es" : ""}:</b>
              {matches.map((m, i) => (
                <div key={i} className="mt-0.5">
                  {m.match_on}: {m.type === "user" ? `${m.full_name || m.email} (account)` : m.type === "booking" ? `booking ${m.reference} (${m.full_name || m.email})` : `property ${m.address || ""}`}
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {!invite.converted_customer_id && (
              <>
                {userMatch && (
                  <Button data-testid="pc-use-existing-customer" size="sm" disabled={busy} variant="outline" className="rounded-full text-xs" onClick={() => createCustomer(userMatch.user_id)}>
                    Use existing customer ({userMatch.email})
                  </Button>
                )}
                <Button data-testid="pc-create-customer" size="sm" disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => createCustomer()}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Create Customer
                </Button>
              </>
            )}
            {invite.converted_customer_id && (
              <Link data-testid="pc-view-customer" to={`/admin/customers/${invite.converted_customer_id}`} className="text-xs font-semibold text-emerald-700 underline self-center">
                Customer created — view profile
              </Link>
            )}
            {!invite.booking_reference ? (
              <Button data-testid="pc-create-booking" size="sm" disabled={busy} className="rounded-full bg-slate-900 text-xs" onClick={() => onCreateBooking(invite)}>
                <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Create Booking
              </Button>
            ) : (
              <span data-testid="pc-booking-linked" className="text-xs font-semibold text-emerald-700 self-center">Booking {invite.booking_reference} linked</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function SlotsManager({ campaignId, onChange, refreshKey, basePath }) {
  const api_base = basePath || `/admin/property-campaigns/${campaignId}/slots`;
  const [slots, setSlots] = useState([]);
  const [techs, setTechs] = useState([]);
  const [view, setView] = useState("grid"); // grid | list
  const [form, setForm] = useState({ date: "", start_time: "09:00", end_time: "11:00", capacity: 1, internal_notes: "", technician_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(api_base).then(({ data }) => {
      setSlots(data.slots);
      setTechs(data.technicians || []);
    }).catch(() => {});
  }, [campaignId, refreshKey]); // refreshKey re-syncs counts after cancel/reschedule
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(api_base, { ...form, technician_id: form.technician_id || null });
      toast.success("Available time added");
      setForm({ ...form, internal_notes: "" });
      load(); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add window"); }
    finally { setBusy(false); }
  };
  const patch = async (sid, body, okMsg) => {
    try {
      const { data } = await api.patch(`${api_base}/${sid}`, body);
      let msg = okMsg;
      if (data?.reassigned_count > 0) msg += ` · ${data.reassigned_count} booked appointment(s) reassigned`;
      if (data?.skipped_manual_override_count > 0) msg += ` · ${data.skipped_manual_override_count} manual-override appointment(s) left unchanged`;
      toast.success(msg); load(); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };
  const changeTech = (s, tid) => {
    const name = tid ? (techs.find((t) => t.id === tid) || {}).name : null;
    if ((s.booked_count || 0) > 0) {
      const msg = tid
        ? `This time already has ${s.booked_count} booked Water Health Check${s.booked_count > 1 ? "s" : ""}. Changing the technician will reassign the existing appointment${s.booked_count > 1 ? "s" : ""} to ${name}. Continue?`
        : `This slot already has ${s.booked_count} booked Water Health Check${s.booked_count > 1 ? "s" : ""}. Removing the technician will leave the appointment${s.booked_count > 1 ? "s" : ""} unassigned until another technician is selected. Continue?`;
      if (!window.confirm(msg)) return;
    }
    patch(s.id, { technician_id: tid || null }, tid ? `Technician set to ${name}` : "Slot set to Unassigned");
  };
  const remove = async (sid) => {
    if (!window.confirm("Delete this unused appointment window?")) return;
    try {
      await api.delete(`${api_base}/${sid}`);
      toast.success("Window deleted"); load(); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className={`${card} mt-6`} data-testid="pc-slots-manager">
      <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3"><Clock className="w-4 h-4 inline mr-1" /> Available Water Health Check Times</h2>
      {!basePath && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="slots-global-note">
          Slots are now <b>global across all campaigns</b> — every property QR code books from this shared pool. Manage centrally in Admin → Availability; changes made here apply everywhere.
        </div>
      )}
      <form onSubmit={add} className="flex flex-wrap items-end gap-2 mb-4">
        <label className="text-xs text-slate-500">Date<br /><input data-testid="pc-slot-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-slate-500">Start<br /><input data-testid="pc-slot-start" required type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-slate-500">End<br /><input data-testid="pc-slot-end" required type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-slate-500">Capacity<br /><input data-testid="pc-slot-capacity" required type="number" min={1} max={50} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-slate-500">Technician<br />
          <select data-testid="pc-slot-technician" value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white max-w-[170px]">
            <option value="">Unassigned</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-500 flex-1 min-w-[140px]">Internal notes (optional)<br /><input data-testid="pc-slot-notes" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" /></label>
        <Button data-testid="pc-slot-add" disabled={busy} type="submit" size="sm" className="rounded-full bg-slate-900"><Plus className="w-3.5 h-3.5 mr-1" /> Add available time</Button>
      </form>
      {techs.length === 0 && (
        <div className="text-xs text-amber-700 mb-3" data-testid="pc-no-techs-hint">
          No active technicians for this campaign's partner yet — times will be created Unassigned.{" "}
          <a href="/field/technicians" target="_blank" rel="noreferrer" className="font-semibold underline">Manage Technicians</a>
        </div>
      )}
      {slots.length === 0 ? (
        <div className="text-sm text-slate-400">No appointment windows yet — homeowners will see the lead-received message until you add some.</div>
      ) : (
        <>
          <div className="flex items-center justify-end gap-1 mb-2" data-testid="pc-slots-view-toggle">
            {["grid", "list"].map((v) => (
              <button
                key={v}
                data-testid={`pc-slots-view-${v}`}
                onClick={() => setView(v)}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${view === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              >
                {v === "grid" ? "Week view" : "List view"}
              </button>
            ))}
          </div>
          {view === "grid" ? (
            <SlotsWeekGrid slots={slots} techs={techs} changeTech={changeTech} patch={patch} remove={remove} />
          ) : (
        <div className="space-y-1.5">
          {slots.map((s) => (
            <div key={s.id} data-testid={`pc-slot-${s.id}`} className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2 text-sm ${s.status === "disabled" ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200"}`}>
              <span className="font-bold text-slate-900 w-28">{fmtSlotDay(s.date)}</span>
              <span className="text-slate-700">{fmtTime12(s.start_time)} – {fmtTime12(s.end_time)}</span>
              <span data-testid={`pc-slot-booked-${s.id}`} className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.booked_count >= s.capacity ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{s.booked_count}/{s.capacity} booked</span>
              <span className="flex items-center gap-1.5" data-testid={`pc-slot-tech-${s.id}`}>
                <span className="text-[11px] font-bold text-slate-400 whitespace-nowrap">Tech</span>
                <select
                  data-testid={`pc-slot-tech-select-${s.id}`}
                  value={s.technician_id || ""}
                  onChange={(e) => changeTech(s, e.target.value || null)}
                  className={`rounded-lg border px-2 py-1 text-xs bg-white max-w-[160px] ${!s.technician_id ? "border-amber-300 text-amber-700 font-semibold" : "border-slate-300"}`}
                >
                  <option value="">⚠ Unassigned</option>
                  {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  {s.technician_id && !techs.some((t) => t.id === s.technician_id) && (
                    <option value={s.technician_id}>{s.technician_name || "Assigned"}</option>
                  )}
                </select>
                {!s.technician_id && (s.booked_count || 0) > 0 && (
                  <span data-testid={`pc-slot-tech-required-${s.id}`} className="text-[11px] font-bold text-rose-600 whitespace-nowrap">⚠ Technician required</span>
                )}
              </span>
              {s.internal_notes && <span className="text-xs text-slate-400 italic">{s.internal_notes}</span>}
              <span className="ml-auto flex gap-2">
                <button data-testid={`pc-slot-toggle-${s.id}`} onClick={() => patch(s.id, { status: s.status === "active" ? "disabled" : "active" }, s.status === "active" ? "Window disabled" : "Window enabled")} className="text-xs font-semibold text-slate-500 hover:text-slate-800">{s.status === "active" ? "Disable" : "Enable"}</button>
                {s.booked_count === 0 && <button data-testid={`pc-slot-delete-${s.id}`} onClick={() => remove(s.id)} className="text-xs font-semibold text-rose-500 hover:text-rose-700">Delete</button>}
              </span>
            </div>
          ))}
        </div>
          )}
        </>
      )}
    </div>
  );
}

// Aug 2026 — weekly calendar layout for the availability pool (the flat
// chronological list was hard to scan). Groups slots by ISO week →
// 7 day columns (Mon–Sun); each cell stacks that day's windows as
// compact cards with the same tech/disable/delete controls.
function SlotsWeekGrid({ slots, techs, changeTech, patch, remove }) {
  const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  // Aug 2026 — per-technician colour coding so coverage (and gaps) jump
  // out. Deterministic: palette assigned by the tech's position in the
  // (name-sorted) technician list; unassigned is always amber.
  const PALETTE = [
    { dot: "bg-cyan-500",    card: "ring-cyan-300 bg-cyan-50",       text: "text-cyan-900" },
    { dot: "bg-violet-500",  card: "ring-violet-300 bg-violet-50",   text: "text-violet-900" },
    { dot: "bg-emerald-500", card: "ring-emerald-300 bg-emerald-50", text: "text-emerald-900" },
    { dot: "bg-orange-500",  card: "ring-orange-300 bg-orange-50",   text: "text-orange-900" },
    { dot: "bg-pink-500",    card: "ring-pink-300 bg-pink-50",       text: "text-pink-900" },
    { dot: "bg-blue-500",    card: "ring-blue-300 bg-blue-50",       text: "text-blue-900" },
    { dot: "bg-teal-500",    card: "ring-teal-300 bg-teal-50",       text: "text-teal-900" },
    { dot: "bg-fuchsia-500", card: "ring-fuchsia-300 bg-fuchsia-50", text: "text-fuchsia-900" },
  ];
  const UNASSIGNED = { dot: "bg-amber-400", card: "ring-amber-300 bg-amber-50", text: "text-amber-900" };
  const colourFor = (tid) => {
    if (!tid) return UNASSIGNED;
    const idx = techs.findIndex((t) => t.id === tid);
    return PALETTE[(idx >= 0 ? idx : Math.abs([...String(tid)].reduce((a, ch) => a + ch.charCodeAt(0), 0))) % PALETTE.length];
  };
  const parse = (d) => new Date(`${d}T00:00:00`);
  const mondayOf = (d) => {
    const dt = parse(d);
    const off = (dt.getDay() + 6) % 7;
    dt.setDate(dt.getDate() - off);
    return dt;
  };
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const weeks = {};
  for (const s of slots) {
    if (!s.date) continue;
    const wk = iso(mondayOf(s.date));
    (weeks[wk] = weeks[wk] || {})[s.date] = [...(weeks[wk]?.[s.date] || []), s];
  }
  const fmtShort = (dt) => dt.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  const todayIso = iso(new Date());

  return (
    <div className="space-y-5" data-testid="pc-slots-week-grid">
      {/* Technician colour legend */}
      <div className="flex flex-wrap items-center gap-3" data-testid="pc-tech-legend">
        {techs.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600" data-testid={`pc-tech-legend-${t.id}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${colourFor(t.id).dot}`} /> {t.name}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
          <span className={`w-2.5 h-2.5 rounded-full ${UNASSIGNED.dot}`} /> Unassigned
        </span>
      </div>
      {Object.keys(weeks).sort().map((wk) => {
        const start = parse(wk);
        const days = [...Array(7)].map((_, i) => {
          const d = new Date(start);
          d.setDate(d.getDate() + i);
          return d;
        });
        const end = days[6];
        return (
          <div key={wk} data-testid={`pc-week-${wk}`}>
            <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 mb-1.5">
              Week of {fmtShort(start)} – {fmtShort(end)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1.5">
              {days.map((d, i) => {
                const dayIso = iso(d);
                const daySlots = (weeks[wk][dayIso] || []).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
                const isToday = dayIso === todayIso;
                return (
                  <div key={dayIso} className={`rounded-xl border min-h-[72px] p-1.5 ${isToday ? "border-cyan-300 bg-cyan-50/40" : daySlots.length ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/60"}`}>
                    <div className={`text-[10px] font-bold mb-1 ${isToday ? "text-cyan-700" : "text-slate-400"}`}>
                      {DAY_NAMES[i]} {fmtShort(d)}
                    </div>
                    <div className="space-y-1.5">
                      {daySlots.map((s) => {
                        const col = colourFor(s.technician_id);
                        return (
                        <div
                          key={s.id}
                          data-testid={`pc-slot-${s.id}`}
                          title={[s.internal_notes, s.recurring_rule_id ? "Auto-generated from the weekly schedule" : null].filter(Boolean).join(" · ")}
                          className={`rounded-lg ring-1 p-1.5 text-[11px] ${s.status === "disabled" ? "ring-slate-200 bg-slate-100 opacity-60" : `${col.card} shadow-sm`}`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className={`inline-flex items-center gap-1 font-bold ${s.status === "disabled" ? "text-slate-500" : col.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${col.dot}`} />
                              {fmtTime12(s.start_time)}–{fmtTime12(s.end_time)}
                            </span>
                            <span data-testid={`pc-slot-booked-${s.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.booked_count >= s.capacity ? "bg-rose-100 text-rose-700" : s.booked_count > 0 ? "bg-emerald-100 text-emerald-700" : "bg-white/70 text-slate-500"}`}>
                              {s.booked_count}/{s.capacity}
                            </span>
                          </div>
                          <select
                            data-testid={`pc-slot-tech-select-${s.id}`}
                            value={s.technician_id || ""}
                            onChange={(e) => changeTech(s, e.target.value || null)}
                            className={`mt-1 w-full rounded-md border px-1 py-0.5 text-[10px] bg-white ${!s.technician_id ? "border-amber-300 text-amber-700 font-semibold" : "border-slate-200 text-slate-700"}`}
                          >
                            <option value="">⚠ Unassigned</option>
                            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            {s.technician_id && !techs.some((t) => t.id === s.technician_id) && (
                              <option value={s.technician_id}>{s.technician_name || "Assigned"}</option>
                            )}
                          </select>
                          <div className="mt-1 flex items-center justify-between">
                            <span className="text-[9px] text-slate-400">{s.recurring_rule_id ? "↻ auto" : ""}</span>
                            <span className="flex gap-1.5">
                              <button data-testid={`pc-slot-toggle-${s.id}`} onClick={() => patch(s.id, { status: s.status === "active" ? "disabled" : "active" }, s.status === "active" ? "Window disabled" : "Window enabled")} className="text-[10px] font-semibold text-slate-400 hover:text-slate-700">
                                {s.status === "active" ? "Disable" : "Enable"}
                              </button>
                              {s.booked_count === 0 && (
                                <button data-testid={`pc-slot-delete-${s.id}`} onClick={() => remove(s.id)} className="text-[10px] font-semibold text-rose-400 hover:text-rose-600">Delete</button>
                              )}
                            </span>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AppointmentsList({ campaignId, appointments, onChange }) {
  const [reschedFor, setReschedFor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [offers, setOffers] = useState([]);
  const [techs, setTechs] = useState([]);
  useEffect(() => {
    if (!appointments.length) return;
    api.get(`/admin/property-campaigns/${campaignId}/inspections`)
      .then(({ data }) => { setInspections(data.inspections || []); setOffers(data.offers || []); })
      .catch(() => {});
    api.get("/field/technicians").then(({ data }) => setTechs(data.technicians || [])).catch(() => {});
  }, [campaignId, appointments.length]);
  const doAssign = async (appt, tid) => {
    try {
      await api.put(`/field/appointments/${appt.id}/assign`, { technician_id: tid || null });
      toast.success(tid ? "Technician assigned" : "Appointment unassigned");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Assignment failed"); }
  };
  const techName = (tid) => (techs.find((t) => t.id === tid) || {}).name;
  const upcoming = appointments.filter((a) => a.status === "scheduled");
  const completed = appointments.filter((a) => a.status === "completed");
  const inspByAppt = Object.fromEntries(inspections.map((i) => [i.appointment_id, i]));
  const offerByInsp = Object.fromEntries(offers.map((o) => [o.inspection_id, o]));
  if (!appointments.length) return null;

  const openResched = async (appt) => {
    setReschedFor(appt);
    const { data } = await api.get(`/admin/property-campaigns/${campaignId}/slots`);
    setSlots((data.slots || []).filter((s) => s.status === "active" && s.id !== appt.slot_id && s.booked_count < s.capacity));
  };
  const doResched = async (slotId) => {
    try {
      await api.post(`/admin/property-campaigns/${campaignId}/appointments/${reschedFor.id}/reschedule`, { slot_id: slotId });
      toast.success("Appointment moved — updated confirmation emailed");
      setReschedFor(null); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Reschedule failed"); }
  };
  const doCancel = async (appt) => {
    if (!window.confirm(`Cancel the Water Health Check for ${appt.street_address}? The slot place will be released.`)) return;
    try {
      await api.post(`/admin/property-campaigns/${campaignId}/appointments/${appt.id}/cancel`);
      toast.success("Appointment cancelled"); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Cancel failed"); }
  };
  const syncFromSm8 = async () => {
    try {
      const { data } = await api.post("/admin/integrations/servicem8/inbound-sync/run");
      const c = data.counts || {};
      toast.success(`ServiceM8 sync: ${c.applied_inbound || 0} updated · ${c.cancelled || 0} cancelled · ${c.conflict_flagged || 0} conflicts · ${c.in_sync || 0} in sync`);
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
  };
  const resolveConflict = async (appt, choose) => {
    try {
      await api.post(`/admin/integrations/servicem8/appointments/${appt.id}/resolve-conflict`, { choose });
      toast.success(choose === "aware" ? "Kept AWARE appointment — ServiceM8 updated" : "Applied ServiceM8 appointment");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Resolve failed"); }
  };

  return (
    <div className={`${card} mt-6`} data-testid="pc-appointments-list">
      <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
        <CalendarClock className="w-4 h-4" /> Upcoming Water Health Checks
        <button onClick={syncFromSm8} data-testid="pc-sm8-sync-now"
          className="ml-auto text-[11px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 normal-case tracking-normal">
          Sync from ServiceM8 now
        </button>
      </h2>
      {upcoming.length === 0 && <div className="text-sm text-slate-400">No scheduled appointments.</div>}
      <div className="space-y-1.5">
        {upcoming.map((a) => (
          <div key={a.id} data-testid={`pc-appt-${a.id}`} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
          {a.sm8_conflict && (
            <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-[12px]" data-testid={`pc-appt-conflict-${a.id}`}>
              <div className="font-bold text-rose-800 mb-1">Sync conflict — this appointment was changed in BOTH systems. Pick the correct one:</div>
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-slate-700"><b>AWARE:</b> {a.sm8_conflict.aware?.date} {fmtTime12(a.sm8_conflict.aware?.start_time)}</span>
                <span className="text-slate-700"><b>ServiceM8:</b> {a.sm8_conflict.servicem8?.date} {fmtTime12(a.sm8_conflict.servicem8?.start_time)}</span>
                <button onClick={() => resolveConflict(a, "aware")} data-testid={`pc-conflict-keep-aware-${a.id}`}
                  className="rounded-full bg-slate-900 text-white text-[11px] font-bold px-3 py-1">Keep AWARE</button>
                <button onClick={() => resolveConflict(a, "servicem8")} data-testid={`pc-conflict-use-sm8-${a.id}`}
                  className="rounded-full bg-white ring-1 ring-slate-300 text-slate-700 text-[11px] font-bold px-3 py-1">Use ServiceM8</button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-bold text-slate-900 w-28">{fmtSlotDay(a.date)}</span>
            <span className="text-slate-700 w-36">{fmtTime12(a.start_time)} – {fmtTime12(a.end_time)}</span>
            <span className="font-semibold text-slate-900">{a.street_address}</span>
            <span className="text-slate-600">{a.first_name} · {a.mobile}</span>
            <EditLeadDialog
              invite={{ ...a, id: a.invite_id }}
              hasAppointment
              onSaved={onChange}
            />
            <span className="flex items-center gap-1.5" data-testid={`pc-appt-tech-${a.id}`}>
              <span className="text-[11px] font-bold text-slate-400 whitespace-nowrap">Tech</span>
              <select
                data-testid={`pc-appt-assign-${a.id}`}
                value={a.assigned_technician_id || ""}
                onChange={(e) => doAssign(a, e.target.value || null)}
                className="rounded-lg border border-slate-300 px-2 py-1 text-xs bg-white max-w-[160px]"
              >
                <option value="">Unassigned</option>
                {techs.filter((t) => t.active && (!a.partner_id || t.partner_id === a.partner_id))
                  .map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                {a.assigned_technician_id && !techs.some((t) => t.id === a.assigned_technician_id && t.active && (!a.partner_id || t.partner_id === a.partner_id)) && (
                  <option value={a.assigned_technician_id}>{techName(a.assigned_technician_id) || "Assigned"}</option>
                )}
              </select>
              {a.technician_assignment_source === "manual_override" && (
                <span data-testid={`pc-appt-manual-override-${a.id}`} title="Manually overridden — the slot's technician differs" className="text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 whitespace-nowrap">Manual override</span>
              )}
              {!a.assigned_technician_id && (
                <span data-testid={`pc-appt-tech-required-${a.id}`} className="text-[11px] font-bold text-rose-600 whitespace-nowrap">⚠ Technician required</span>
              )}
            </span>
            {a.confirmation_email_status === "failed" && <span data-testid={`pc-appt-email-failed-${a.id}`} className="text-xs font-bold text-rose-600">confirmation email failed</span>}
            <span className="ml-auto flex gap-2">
              <button data-testid={`pc-appt-resched-${a.id}`} onClick={() => openResched(a)} className="text-xs font-semibold text-cyan-700 hover:text-cyan-900">Change time</button>
              <button data-testid={`pc-appt-cancel-${a.id}`} onClick={() => doCancel(a)} className="text-xs font-semibold text-rose-500 hover:text-rose-700">Cancel</button>
            </span>
            <a data-testid={`pc-open-inspection-${a.id}`} href={`/field/inspection/${a.id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-emerald-700 hover:text-emerald-900">Open inspection</a>
            {reschedFor?.id === a.id && (
              <div data-testid="pc-resched-panel" className="w-full mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 flex flex-wrap gap-2">
                {slots.length === 0 && <span className="text-xs text-slate-400">No other windows with capacity — add one above first.</span>}
                {slots.map((s) => (
                  <button key={s.id} data-testid={`pc-resched-to-${s.id}`} onClick={() => doResched(s.id)} className="text-xs font-semibold rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-cyan-800 hover:bg-cyan-50">
                    {fmtSlotDay(s.date)} · {fmtTime12(s.start_time)}–{fmtTime12(s.end_time)}{" · "}
                    <span className={s.technician_name ? "text-slate-600" : "text-amber-700"}>{s.technician_name || "Unassigned"}</span>
                  </button>
                ))}
                <button onClick={() => setReschedFor(null)} className="text-xs text-slate-400 underline">close</button>
              </div>
            )}
          </div>
          </div>
        ))}
      </div>
      {completed.length > 0 && (
        <div className="mt-5" data-testid="pc-completed-inspections">
          <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">Completed Water Health Checks</h3>
          <div className="space-y-1.5">
            {completed.map((a) => {
              const insp = inspByAppt[a.id];
              const offer = insp ? offerByInsp[insp.id] : null;
              return (
                <div key={a.id} data-testid={`pc-completed-${a.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/40 px-3 py-2 text-sm">
                  <span className="font-bold text-slate-900 w-28">{fmtSlotDay(a.date)}</span>
                  <span className="font-semibold text-slate-900">{a.street_address}</span>
                  <span className="text-slate-600">{a.first_name}</span>
                  {a.assigned_technician_id && techName(a.assigned_technician_id) && (
                    <span className="text-xs text-slate-500" data-testid={`pc-completed-tech-${a.id}`}>Tech: <b>{techName(a.assigned_technician_id)}</b></span>
                  )}
                  {insp && (
                    <span className="text-xs text-slate-500">
                      {insp.condition_score && <b data-testid={`pc-score-${a.id}`} style={{ color: insp.condition_score.score >= 85 ? "#059669" : insp.condition_score.score >= 70 ? "#65a30d" : insp.condition_score.score >= 50 ? "#d97706" : "#ea580c" }}>Score {insp.condition_score.score}/100 · </b>}
                      {insp.review_status === "approved"
                        ? <>Report: <b className={insp.report_email_status === "sent" ? "text-emerald-700" : "text-rose-600"}>{insp.report_email_status || "approved"}</b></>
                        : insp.review_status === "returned"
                          ? <b className="text-slate-600">Returned to technician</b>
                          : <b className="text-amber-700">{(insp.ai_report || {}).status === "failed" ? "AI failed — review" : (insp.ai_report || {}).status === "flagged" ? "Safety flagged — review" : "Pending review"}</b>}
                      {offer && <> · Offer: <b className={offer.status === "redeemed" ? "text-emerald-700" : "text-slate-700"}>{offer.status}</b> ({offer.click_count || 0} clicks{offer.booking_reference ? ` · ${offer.booking_reference}` : ""})</>}
                    </span>
                  )}
                  <span className="ml-auto flex gap-2">
                    {insp && <Link data-testid={`pc-review-${a.id}`} className={`text-xs font-bold ${insp.review_status === "approved" ? "text-slate-500" : "text-amber-700"}`} to={`/admin/property-campaigns/${campaignId}/inspections/${insp.id}/review`}>{insp.review_status === "approved" ? "View review" : "Review report"}</Link>}
                    {insp?.report_token && insp.review_status === "approved" && <a data-testid={`pc-view-report-${a.id}`} className="text-xs font-semibold text-cyan-700" href={`${api.defaults.baseURL}/public/report/${insp.report_token}`} target="_blank" rel="noreferrer">View report</a>}
                    {insp && <ResendReportButton campaignId={campaignId} inspection={insp} onSent={onChange} />}
                    <a className="text-xs font-semibold text-slate-500" href={`/field/inspection/${a.id}`} target="_blank" rel="noreferrer">Inspection</a>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineChart({ campaignId, mailedAt }) {
  const [days, setDays] = useState(null);
  useEffect(() => {
    api.get(`/admin/property-campaigns/${campaignId}/timeline`)
      .then(({ data }) => setDays(data.days)).catch(() => setDays([]));
  }, [campaignId]);
  if (!days) return null;
  if (!days.length) return (
    <div className={`${card} mt-6 text-sm text-slate-500`} data-testid="pc-timeline-empty">
      Scan / lead timeline will appear here once the campaign is mailed or activity starts.
    </div>
  );
  const rows = days.map((d) => ({ ...d, label: new Date(d.date).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) }));
  return (
    <div className={`${card} mt-6`} data-testid="pc-timeline-chart">
      <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">Daily Scans &amp; Leads {mailedAt ? `(since mailed ${fmtD(mailedAt)})` : ""}</h2>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={rows} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="unique_scans" name="Unique properties scanned" fill="#0e7490" radius={[3, 3, 0, 0]} />
          <Bar dataKey="leads" name="Leads captured" fill="#059669" radius={[3, 3, 0, 0]} />
          <Bar dataKey="total_scans" name="Total scans" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function PropertyCampaignDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(searchParams.get("invite") || null);
  const [selected, setSelected] = useState({});
  const [bookingFor, setBookingFor] = useState(null); // invite for CreateBookingModal
  const [refreshKey, setRefreshKey] = useState(0);
  // Sep 2026 — one vocabulary switch for the whole screen. Campaigns
  // without mail_format are A4, exactly as before.
  const mailPiece = data?.campaign?.mail_format === "postcard" ? "Postcard" : "Letter";
  const [emailDelivery, setEmailDelivery] = useState({});
  const [deliveryRestricted, setDeliveryRestricted] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/property-campaigns/${id}`).then(({ data: d }) => setData(d)).catch(() => {});
    api.get(`/admin/property-campaigns/${id}/email-delivery`).then(({ data: d }) => { setEmailDelivery(d.emails || {}); setDeliveryRestricted(!!d.read_restricted); }).catch(() => {});
    setRefreshKey((k) => k + 1);
  }, [id]);
  useEffect(load, [load]);

  // Ref lookup deep-link: scroll the targeted invite row into view
  useEffect(() => {
    const iid = searchParams.get("invite");
    if (!data || !iid) return;
    setTimeout(() => {
      document.querySelector(`[data-testid="pc-invite-${iid}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  }, [data, searchParams]);

  // Staff Sync Alerts "View booking" deep-link: scroll + highlight the
  // targeted appointment row into view.
  useEffect(() => {
    const aid = searchParams.get("appt");
    if (!data || !aid) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="pc-appt-${aid}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 4000);
    }, 300);
  }, [data, searchParams]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const doPreview = async () => {
    setBusy(true);
    try { const { data: p } = await api.post(`/admin/property-campaigns/${id}/import-preview`, { csv_text: csvText }); setPreview(p); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const doCommit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/property-campaigns/${id}/import-commit`, { rows: preview.valid });
      toast.success(`Imported ${r.created} properties (${r.skipped} skipped)`);
      setPreview(null); setCsvText(""); setFileName(""); load();
    } catch (e) { toast.error("Import failed"); }
    finally { setBusy(false); }
  };
  const genLetters = async (onlySelected = false) => {
    setBusy(true);
    try {
      const body = onlySelected && selectedIds.length ? { invite_ids: selectedIds } : {};
      const res = await api.post(`/admin/property-campaigns/${id}/generate-letters`, body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `AWARE-${data.campaign.code}-Letters.pdf`; a.click();
      URL.revokeObjectURL(url); load();
      toast.success("Letters PDF generated");
    } catch { toast.error("Letter generation failed"); }
    finally { setBusy(false); }
  };
  const previewLetter = () => {
    const base = api.defaults.baseURL;
    const token = localStorage.getItem("scwt_token");
    fetch(`${base}/admin/property-campaigns/${id}/letter-preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("Letter preview failed"));
  };
  const markMailed = async (onlySelected = false) => {
    const scope = onlySelected && selectedIds.length ? `${selectedIds.length} selected properties` : "this campaign";
    if (!window.confirm(`Mark ${scope} as mailed? This records the mailed date and won't overwrite properties already marked mailed.`)) return;
    setBusy(true);
    try {
      const body = onlySelected && selectedIds.length ? { invite_ids: selectedIds } : {};
      const { data: r } = await api.post(`/admin/property-campaigns/${id}/mark-mailed`, body);
      toast.success(`${r.marked} properties marked as mailed`);
      setSelected({}); load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const testQr = (inv) => window.open(`/check/${inv.qr_token}?preview=1`, "_blank");

  if (!data) return <div className="p-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const { campaign: c, invites, stats } = data;
  const appointments = data.appointments || [];
  const apptByInvite = {};
  for (const a of appointments) {
    if (!apptByInvite[a.invite_id] || a.status === "scheduled") apptByInvite[a.invite_id] = a;
  }
  const daysSinceMailed = c.mailed_at ? Math.floor((Date.now() - new Date(c.mailed_at).getTime()) / 86400000) : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="property-campaign-detail">
      <Link to="/admin/property-campaigns" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All campaigns</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{c.name} <span className="font-mono text-sm text-slate-500">{c.code}</span></h1>
          <div data-testid="pc-mailed-info" className="text-sm text-slate-600 mt-1">
            {c.mailed_at ? <>Mailed: <b>{fmtD(c.mailed_at)}</b> · Days Since Mailed: <b>{daysSinceMailed}</b></> : "Not yet mailed"}
          </div>
          {/* Sep 2026 — mail format is a campaign-level fact. Switching is
              blocked by the API once anything has been LIVE mailed. */}
          <div className="mt-1.5 flex items-center gap-2" data-testid="pc-mail-format">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${mailPiece === "Postcard" ? "bg-cyan-100 text-cyan-900" : "bg-slate-100 text-slate-700"}`}>
              {mailPiece === "Postcard" ? "Postcard · 210×130mm" : "A4 Letter"}
            </span>
            <button
              data-testid="pc-mail-format-switch"
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 underline"
              onClick={async () => {
                const next = mailPiece === "Postcard" ? "a4_letter" : "postcard";
                if (!window.confirm(`Change this campaign's mail format to ${next === "postcard" ? "POSTCARD" : "A4 LETTER"}?\n\nThis is blocked once any recipient has been LIVE mailed.`)) return;
                try {
                  await api.patch(`/admin/property-campaigns/${id}`, { mail_format: next });
                  toast.success(`Mail format set to ${next === "postcard" ? "Postcard" : "A4 Letter"}`);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Could not change mail format"); }
              }}
            >
              Change format
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <RenameCampaignButton campaignId={id} onRenamed={load} />
          <SafeDeleteCampaignButton campaignId={id} />
          <Link to="/admin/settings/servicem8" data-testid="pc-detail-servicem8-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900 rounded-full border border-cyan-200 bg-cyan-50 px-3.5 py-2">
            <CalendarClock className="w-4 h-4" /> ServiceM8 Scheduling
          </Link>
          {/* Sep 2026 — postcard campaigns must never say "Letters". */}
          <Button data-testid="pc-preview-letter" disabled={!invites.length} onClick={previewLetter} variant="outline" className="rounded-full"><Eye className="w-4 h-4 mr-1" /> Preview {mailPiece}</Button>
          <Button data-testid="pc-generate-letters" disabled={busy || !invites.length} onClick={() => genLetters(false)} className="rounded-full bg-slate-900"><FileDown className="w-4 h-4 mr-1" /> Generate {mailPiece}s{selectedIds.length ? " (all)" : ""}</Button>
          {selectedIds.length > 0 && (
            <Button data-testid="pc-generate-selected" disabled={busy} onClick={() => genLetters(true)} variant="outline" className="rounded-full"><FileDown className="w-4 h-4 mr-1" /> Generate selected ({selectedIds.length})</Button>
          )}
          <Button data-testid="pc-mark-mailed" disabled={busy || !invites.length} onClick={() => markMailed(false)} variant="outline" className="rounded-full"><MailCheck className="w-4 h-4 mr-1" /> Mark Campaign as Mailed</Button>
          {selectedIds.length > 0 && (
            <Button data-testid="pc-mark-mailed-selected" disabled={busy} onClick={() => markMailed(true)} variant="outline" className="rounded-full"><MailCheck className="w-4 h-4 mr-1" /> Mark selected mailed ({selectedIds.length})</Button>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-2.5">
        <StatCard tid="pc-stat-imported" label="Properties" value={stats.properties_imported} />
        <StatCard tid="pc-stat-letters" label={`${mailPiece}s`} value={stats.letters_generated} />
        <StatCard tid="pc-stat-mailed" label="Mailed" value={stats.mailed} />
        <StatCard tid="pc-stat-scanned" label="Unique Scanned" value={stats.unique_scanned} sub={`${stats.total_scans} total scans`} />
        <StatCard tid="pc-stat-checks" label="Checks" value={stats.eligibility_checks} sub={`Y ${stats.answer_yes} · ? ${stats.answer_not_sure} · N ${stats.answer_no}`} />
        <StatCard tid="pc-stat-leads" label="Leads" value={stats.leads_captured} />
      </div>
      <div className="mt-2.5 grid grid-cols-3 sm:grid-cols-7 gap-2.5">
        <StatCard tid="pc-stat-appointments" label="Appointments" value={stats.appointments_scheduled} />
        <StatCard tid="pc-stat-appointment-rate" label="Appt Rate" value={pct(stats.appointment_rate)} sub="appts / leads" />
        <StatCard tid="pc-stat-customers" label="Customers Created" value={stats.customers_created} />
        <StatCard tid="pc-stat-bookings" label="Bookings Created" value={stats.bookings_created} />
        <StatCard tid="pc-stat-scan-rate" label="Scan Rate" value={pct(stats.scan_rate)} sub="scanned / mailed" />
        <StatCard tid="pc-stat-lead-rate" label="Lead Rate" value={pct(stats.lead_rate)} sub="leads / mailed" />
        <StatCard tid="pc-stat-scan-to-lead" label="Scan → Lead" value={pct(stats.scan_to_lead_rate)} sub="leads / scanned" />
      </div>

      {/* Sep 2026 — the A/B panel is letter-copy specific (Version A/B
          letter text). Postcards have a single approved artwork pair, so
          showing it here would be confusing and irrelevant. */}
      {mailPiece !== "Postcard" && (
        <LetterABPanel campaignId={id} campaign={c} onChanged={load} />
      )}

      {mailPiece === "Postcard" && (
        <PostcardStudioCampaignPanel campaignId={id} />
      )}

      <CampaignMailout campaignId={id} onChanged={load} />

      <details className="mt-6 mb-4" data-testid="pc-slots-advanced">
        <summary className="cursor-pointer text-xs font-bold text-slate-500 hover:text-slate-700 uppercase tracking-wider">
          Advanced — one-off dates, sick days & booked windows
        </summary>
        <div className="mt-3">
          <SlotsManager campaignId={id} onChange={load} refreshKey={refreshKey} />
        </div>
      </details>
      <AppointmentsList campaignId={id} appointments={appointments} onChange={load} />

      <TimelineChart campaignId={id} mailedAt={c.mailed_at} />

      <div className={`${card} mt-6`}>
        <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-2"><Upload className="w-4 h-4 inline mr-1" /> Import Properties (CSV)</h2>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input ref={fileRef} data-testid="pc-csv-file" type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const reader = new FileReader();
              reader.onload = () => { setCsvText(String(reader.result || "")); setPreview(null); setFileName(f.name); };
              reader.readAsText(f);
              e.target.value = "";
            }} />
          <Button data-testid="pc-upload-csv" type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4 mr-1" /> Upload CSV file
          </Button>
          {fileName && <span data-testid="pc-csv-filename" className="text-xs font-semibold text-slate-600">{fileName} loaded</span>}
          <span className="text-xs text-slate-400">or paste CSV below · columns: street_address, suburb, state, postcode</span>
        </div>
        <textarea data-testid="pc-csv" rows={4} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder={"street_address,suburb,state,postcode\n28 Redwood Drive,Doonan,QLD,4562"} className="w-full rounded-xl border border-slate-300 p-3 font-mono text-xs" />
        <div className="mt-2 flex gap-2">
          <Button data-testid="pc-preview-import" disabled={busy || !csvText.trim()} onClick={doPreview} variant="outline" className="rounded-full">Preview import</Button>
          {preview && <Button data-testid="pc-commit-import" disabled={busy || !preview.importable} onClick={doCommit} className="rounded-full bg-cyan-600">Confirm import ({preview.importable} of {preview.valid.length})</Button>}
        </div>
        {preview && (
          <div data-testid="pc-import-preview" className="mt-3 text-sm text-slate-700">
            {preview.total_rows} rows · {preview.valid.length} valid · {preview.invalid.length} invalid · {preview.duplicate_count} duplicates · {preview.existing_matches} existing matches{preview.inspection_excluded ? ` · ${preview.inspection_excluded} inspected <24 months (excluded)` : ""}
            {preview.invalid.map((r) => <div key={r.line} className="text-rose-600 text-xs">Line {r.line}: {r.error}</div>)}
            {preview.valid.filter((r) => r.skip || r.existing_campaign || r.existing_aware_property).map((r) => (
              <div key={r.line} className="text-amber-700 text-xs">
                Line {r.line}: {r.street_address} — {r.inspected_within_24_months ? "inspected within the last 24 months (excluded)" : r.duplicate_of_line ? `duplicate of line ${r.duplicate_of_line} (skipped)` : r.existing_aware_property ? "already an AWARE property/customer (skipped)" : r.already_converted ? "already converted in another campaign (skipped)" : "in another campaign (will import)"}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${card} mt-6 overflow-x-auto`}>
        <div className="mb-2 text-[11px] font-semibold text-slate-500" data-testid="pc-invite-checkbox-note">
          Checkboxes control <b>Generate selected</b> / <b>Mark selected mailed</b> only — they do <b>not</b> control the Bing Mailout&apos;s &quot;Approve &amp; Send&quot;, which always includes ALL Ready-to-Mail recipients.
        </div>
        <table className="w-full text-sm" data-testid="pc-invite-table">
          <thead><tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
            <th className="py-2 pr-2 w-6"><input type="checkbox" data-testid="pc-select-all" checked={invites.length > 0 && selectedIds.length === invites.length} onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(invites.map((i) => [i.id, true])) : {})} /></th>
            <th className="py-2 pr-3">Property</th><th className="pr-3">Status</th><th className="pr-3">Scans</th><th className="pr-3">Lead</th><th className="pr-3">Appointment</th><th className="pr-3">Actions</th><th className="w-6" />
          </tr></thead>
          <tbody>
            {invites.map((i) => {
              const appt = apptByInvite[i.id];
              const st = statusOf(i, appt);
              const isOpen = expanded === i.id;
              return [
                <tr key={i.id} data-testid={`pc-invite-${i.id}`} className="border-t border-slate-100">
                  <td className="py-2 pr-2"><input type="checkbox" data-testid={`pc-select-${i.id}`} checked={!!selected[i.id]} onChange={(e) => setSelected({ ...selected, [i.id]: e.target.checked })} /></td>
                  <td className="py-2 pr-3 font-semibold text-slate-900">{i.street_address}, {i.suburb}
                    {i.letter_version && (
                      <span data-testid={`pc-letter-version-${i.id}`}
                        className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold align-middle ${i.letter_version === "B" ? "bg-violet-100 text-violet-800 border border-violet-300" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
                        {i.letter_version === "B" ? "Ver B" : "Ver A"}
                      </span>
                    )}
                  </td>
                  <td className="pr-3"><span data-testid={`pc-status-${i.id}`} className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[st]}`}>{st}</span></td>
                  <td className="pr-3 text-slate-600">{i.first_scan_at ? i.scan_count : "—"}</td>
                  <td className="pr-3 text-slate-600">{i.lead_created_at ? `${i.first_name} / ${i.mobile}` : "—"}</td>
                  <td className="pr-3 text-slate-700" data-testid={`pc-appt-cell-${i.id}`}>
                    {appt ? <span className="font-semibold">{fmtSlotDay(appt.date)} · {fmtTime12(appt.start_time)}–{fmtTime12(appt.end_time)}</span> : i.lead_created_at ? <span className="text-slate-400">Not selected</span> : "—"}
                  </td>
                  <td className="pr-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`pc-test-qr-${i.id}`} onClick={() => testQr(i)} className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900">
                        <QrCode className="w-3.5 h-3.5" /> Test QR
                      </button>
                      <EditLeadDialog invite={i} hasAppointment={!!appt} onSaved={load} />
                    </div>
                  </td>
                  <td>
                    <button data-testid={`pc-expand-${i.id}`} onClick={() => setExpanded(isOpen ? null : i.id)} className="text-slate-400 hover:text-slate-700">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </td>
                </tr>,
                isOpen && (
                  <tr key={`${i.id}-detail`} className="border-t border-slate-50 bg-slate-50/60">
                    <td colSpan={8} className="px-3 py-3">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Timeline</div>
                          <InviteTimeline invite={i} appt={appt} />
                          <EmailDeliveryList emails={emailDelivery[i.id]} readRestricted={deliveryRestricted} />
                        </div>
                        <div>
                          {i.lead_created_at ? (
                            <ConvertPanel campaignId={id} invite={i} onDone={load} onCreateBooking={setBookingFor} />
                          ) : (
                            <div className="text-xs text-slate-400 mt-2">Convert actions appear once a lead is captured.</div>
                          )}
                          {(i.admin_notification_status || i.partner_notification_status) && (
                            <div data-testid={`pc-notif-status-${i.id}`} className="mt-3 text-[11px] text-slate-500">
                              Notifications — Admin: <b className={i.admin_notification_status === "sent" ? "text-emerald-700" : "text-slate-700"}>{i.admin_notification_status || "—"}</b>
                              {" · "}Partner: <b className={i.partner_notification_status === "sent" ? "text-emerald-700" : "text-slate-700"}>{i.partner_notification_status || "—"}</b>
                            </div>
                          )}
                          {appt && (
                            <div data-testid={`pc-appt-notif-${i.id}`} className="mt-1 text-[11px] text-slate-500">
                              Appointment emails — Confirmation: <b className={appt.confirmation_email_status === "sent" ? "text-emerald-700" : "text-slate-700"}>{appt.confirmation_email_status}</b>
                              {" · "}Reminder: <b className={appt.reminder_email_status === "sent" ? "text-emerald-700" : "text-slate-700"}>{appt.reminder_email_status}</b>
                            </div>
                          )}
                          <InviteResetButton invite={i} onDone={load} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      <CreateBookingModal
        open={!!bookingFor}
        onClose={() => setBookingFor(null)}
        initialCustomer={bookingFor ? {
          name: bookingFor.first_name || "",
          mobile: bookingFor.mobile || "",
          email: bookingFor.email || "",
          address: `${bookingFor.street_address}, ${bookingFor.suburb} ${bookingFor.state} ${bookingFor.postcode}`,
        } : null}
        onCreated={async (booking) => {
          if (bookingFor && booking?.reference) {
            try {
              await api.post(`/admin/property-campaigns/${id}/invites/${bookingFor.id}/link-booking`, { booking_reference: booking.reference });
              toast.success(`Booking ${booking.reference} linked to campaign lead`);
            } catch { toast.error("Booking created but campaign link failed — link manually"); }
          }
          load();
        }}
      />
    </div>
  );
}
